import {getFeature} from '@kakaoent/feature-flag';
import axios from 'axios';
import type {Request, Response} from 'express';
import {IncomingHttpHeaders} from 'http';
import isNil from 'lodash/isNil';
import LZString from 'lz-string';
import {format} from 'util';
import {Logger} from 'winston';
import {getEnv} from '../common/env/env';
import {requestContext} from '../common/requestContext';
import {BFF_LOGGING_TYPES, ERROR_LOG_TYPES, LogType} from './constant';
import {sendSseLog} from './sendSseLog.function';

// winston, console 기준 로그레벨.
export enum LogLevelType {
  None = 'none',
  Debug = 'debug',
  Info = 'info',
  Warn = 'warn',
  Err = 'error',
}

export interface ClientLog {
  date: Date;
  level: LogLevelType;
  message: string;
}
const LOG_LEVEL_PRIORITY: {[key: string]: number} = {
  [LogLevelType.None]: -1,
  [LogLevelType.Err]: 0,
  [LogLevelType.Warn]: 1,
  [LogLevelType.Info]: 2,
  // 윈스톤에는 Verbose(3) 타입이 있는데 Console 에는 없어서 뺌
  [LogLevelType.Debug]: 4,
};

const POST_LOG_API_TIMEOUT = 1000;
const FLUSH_BUFFER_SCHEDULER_TIMEOUT = 5000;
const LOG_SENDING_LIMIT_LENGTH = 100000; // 100k

let _serverLogger: Logger;
const _buffer: ClientLog[] = [];

export function initServerLogger({serverLogger}: {serverLogger: Logger}) {
  if (_serverLogger) {
    return;
  }
  _serverLogger = serverLogger;
}

if (
  typeof window !== 'undefined' &&
  !process?.env?.JEST_WORKER_ID &&
  // @ts-expect-error
  !window.Cypress
) {
  scheduledflushBuffer();

  async function scheduledflushBuffer() {
    await flushBuffer();
    setTimeout(scheduledflushBuffer, FLUSH_BUFFER_SCHEDULER_TIMEOUT);
  }

  /**
   * 쌓인 로그를 서버로 전송
   */
  async function flushBuffer() {
    if (
      !getEnv()?.sendBrowserLogToServer &&
      !requestContext?.requestInfo?.logging
    ) {
      _buffer.length = 0;
      return;
    }

    if (_buffer.length > 0) {
      const payload = [];
      let dataSize = 0;

      // 버퍼의 마지막 로그부터 payload 에 차곡차곡 담는다.
      // 만약 리미트에 걸리면 걸리기 전 까지만 Flush 한다
      // 버퍼에 남아있는 로그는 다음에 Flush 할 때 전송한다. 단, 페이지를 빠져나갈 땐(unload) 포기한다
      for (let index = _buffer.length - 1; index >= 0; index--) {
        const logItem = _buffer[index];

        if (logItem.message.length >= LOG_SENDING_LIMIT_LENGTH) {
          if (canLogging(LogLevelType.Warn) && getEnv().showConsoleLogBrowser) {
            console.warn('아이템 사이즈가 너무 큽니다', logItem);
          }
          _buffer.splice(index, 1);
          continue;
        }
        dataSize += logItem.message.length;
        // payload 가 리미트까지 차면 그만 넣는다
        if (dataSize > LOG_SENDING_LIMIT_LENGTH) {
          if (canLogging(LogLevelType.Warn) && getEnv().showConsoleLogBrowser) {
            console.warn('데이터 사이즈가 너무 큽니다', dataSize);
          }
          break;
        }
        _buffer.splice(index, 1);
        payload.unshift(logItem);
      }
      if (!payload.length) {
        return;
      }
      try {
        await axios.post(
          '/l',
          {
            data: LZString.compressToBase64(JSON.stringify(payload)),
          },
          {timeout: POST_LOG_API_TIMEOUT}
        );
      } catch (e) {
        _buffer.push(...payload);
        console.warn('Unexpected flush error', e);
      }
    }
  }

  window.addEventListener('unload', async () => {
    await flushBuffer();
  });
}

export function canLogging(level: LogLevelType) {
  if (requestContext?.requestInfo?.logging) {
    return true;
  }
  if (process?.env?.JEST_WORKER_ID) {
    return false;
  }
  if (typeof window !== 'undefined') {
    if (
      LOG_LEVEL_PRIORITY[getEnv().logLevelBrowser || LogLevelType.Debug] <
      LOG_LEVEL_PRIORITY[level]
    ) {
      return false;
    }
    return true;
  } else {
    if (
      LOG_LEVEL_PRIORITY[getEnv().logLevelServer || LogLevelType.None] <
      LOG_LEVEL_PRIORITY[level]
    ) {
      return false;
    }
    if (!_serverLogger) {
      return false;
    }
    return true;
  }
}

function log(
  level: LogLevelType,
  message: string | Record<string, unknown>,
  forceLogging?: boolean,
  extra?: Record<string, any>
) {
  if (!message) {
    return;
  }
  if (!forceLogging && !canLogging(level)) {
    return;
  }
  const _message = `${
    typeof message === 'object'
      ? axios.isAxiosError(message)
        ? message.toJSON()
        : JSON.stringify(message)
      : message
  }`;
  try {
    if (typeof window !== 'undefined') {
      if (getEnv().showConsoleLogBrowser) {
        console[level as Exclude<LogLevelType, LogLevelType.None>](
          // 브라우저의 console.log 는 %j 를 지원하지 않는다. 이쁘게 오브젝트 보여주려면 %o 를 사용해야함
          message,
          extra
        );
      }

      if (
        requestContext?.requestInfo?.logging ||
        getEnv().sendBrowserLogToServer
      ) {
        _buffer.push({
          date: new Date(),
          level,
          message: _message,
        });
      }
    } else {
      if (
        requestContext?.requestInfo?.logging ||
        getEnv().showConsoleLogServer
      ) {
        try {
          _serverLogger.log(level, _message, extra);
        } catch (e) {
          _serverLogger.warn(
            'unexpected logging error. input : %s, error : %j',
            JSON.stringify(message, e),
            e
          );
        }
      }
    }
  } catch (e) {
    // 끝끝내 로그를 남기지 못하면 warn 정도로 error 로그를 남겨준다(거의 일어날 일 없음)
    console.warn('Unexpected logging error', e);
  }
}

function info(
  message: string | Record<string, unknown>,
  extra?: Record<string, any>
) {
  log(LogLevelType.Info, message, false, extra);
}

function warn(
  message: string | Record<string, unknown>,
  extra?: Record<string, any>
) {
  log(LogLevelType.Warn, message, false, extra);
}

function debug(
  message: string | Record<string, unknown>,
  extra?: Record<string, any>
) {
  log(LogLevelType.Debug, message, false, extra);
}

function err(
  message: string | Record<string, unknown>,
  e?: Error,
  extra?: Record<string, any>
) {
  // TODO: Sentry 설정되면 추가
  // if (args && Array.isArray(args)) {
  //   args
  //     .filter(arg => arg instanceof Error)
  //     .forEach(arg => Sentry.captureException(arg));
  // }
  log(LogLevelType.Err, message, false, {
    ...(e ? {error: `${e.toString()}: ${e.stack}`} : {}),
    ...(extra && extra),
  });
}

const logger = {
  log,
  info,
  warn,
  debug,
  err,
};

export default logger;

export function logPageAccess({req, res}: {req: Request; res: Response}) {
  if (!canLogging(LogLevelType.Info)) {
    return;
  }
  // 서버사이드가 아니면 로그를 남기지 않는다
  if (!req) {
    return;
  }
  info(
    format(
      'path : %s, query : %j, headers : %j, requestInfo : %j',
      req.path,
      req.query,
      {headers: getUsefulHeaders(req.headers)},
      requestContext.requestInfo
    ),
    {
      logType: LogType.AccessLog,
    }
  );
}

export function logBff(
  logType: LogType,
  message: string | Record<string, any>
) {
  if (ERROR_LOG_TYPES.includes(logType)) {
    err(message, undefined, {logType});
  } else {
    info(message, {
      logType,
    });
  }

  // logBff는 서버사이드 호출이 보장되어 getFeature()를 사용합니다.
  if (getFeature().logBffTracing && BFF_LOGGING_TYPES.includes(logType)) {
    const simpleBffLog: any[] = [logType];
    if (typeof message === 'string') {
      simpleBffLog.push(message);
    } else if (message && typeof message === 'object') {
      'url' in message && simpleBffLog.push(`url=${message.url}`);
      'status' in message && simpleBffLog.push(`status=${message.status}`);
      'body' in message &&
        !isNil(message?.body?.result_code) &&
        simpleBffLog.push(`result_code=${message.body.result_code}`);
    }
    log(LogLevelType.Info, simpleBffLog.join(', '), true);
  }
  try {
    sendSseLog({
      type: logType,
      message,
    });
  } catch (e) {
    console.error('sendSseLog error', e);
  }
}

function getUsefulHeaders(headers: IncomingHttpHeaders) {
  const usefulHeaders = [];
  for (const key of Object.keys(headers)) {
    if (USEFUL_KEYS.includes(key)) {
      usefulHeaders.push(`${key}=${headers[key]}`);
    }
  }
  return usefulHeaders;
}

const USEFUL_KEYS = [
  'user-agent',
  // 'x-stoken',   // stoken 은 민감한 토큰이기 때문에 남기면 안됨
  'x-device-id',
  'x-user-id',
  'referrer',
  'referer',
];

export function getServerLog() {
  return _serverLogger;
}
